import React from 'react';
import {View, Text, StyleSheet, TouchableOpacity, Platform} from 'react-native';
import {FontAwesomeIcon} from '@fortawesome/react-native-fontawesome';
import {faTimes, faCheckCircle, faExclamationTriangle, faInfoCircle} from '@fortawesome/free-solid-svg-icons';
import {Fonts} from '../config/fonts';

export interface GameNotification {
  id: string;
  message: string;
  type?: 'info' | 'success' | 'warning' | 'error';
  duration?: number;
}

interface GameNotificationProps {
  notification: GameNotification;
  onDismiss: (id: string) => void;
}

const GameNotification: React.FC<GameNotificationProps> = ({
  notification,
  onDismiss,
}) => {
  // Couleurs narratives selon le type (style livre)
  const getStyles = () => {
    switch (notification.type) {
      case 'success':
        return {
          backgroundColor: '#E8F5E9', // Vert très pâle / papier
          borderColor: '#66BB6A', // Vert doux
          accentColor: '#2E7D32', // Vert plus foncé
          icon: faCheckCircle,
        };
      case 'warning':
        return {
          backgroundColor: '#FFF3E0', // Orange pâle / papier
          borderColor: '#FFB74D', // Orange doux
          accentColor: '#E65100', // Orange foncé
          icon: faExclamationTriangle,
        };
      case 'error':
        return {
          backgroundColor: '#FFEBEE', // Rouge pâle / papier
          borderColor: '#EF5350', // Rouge doux
          accentColor: '#C62828', // Rouge foncé
          icon: faExclamationTriangle,
        };
      default:
        return {
          backgroundColor: '#E3F2FD', // Bleu pâle / papier
          borderColor: '#64B5F6', // Bleu doux
          accentColor: '#1565C0', // Bleu foncé
          icon: faInfoCircle,
        };
    }
  };

  const styleConfig = getStyles();

  return (
    <View style={[
      styles.container,
      {
        backgroundColor: styleConfig.backgroundColor,
        borderColor: styleConfig.borderColor,
      }
    ]}>
      {/* Indicateur gauche décoratif */}
      <View style={[styles.indicator, {backgroundColor: styleConfig.accentColor}]} />
      
      {/* Contenu */}
      <View style={styles.content}>
        {/* Icône narrative (optionnelle, discrète) */}
        <FontAwesomeIcon 
          icon={styleConfig.icon} 
          size={16} 
          color={styleConfig.accentColor} 
          style={styles.icon}
        />
        
        {/* Message narratif */}
        <Text style={[styles.message, {color: '#2C2C2C'}]}>
          {notification.message}
        </Text>
      </View>

      {/* Bouton de fermeture discret */}
      <TouchableOpacity
        style={styles.dismissButton}
        onPress={() => onDismiss(notification.id)}
        hitSlop={{top: 8, bottom: 8, left: 8, right: 8}}>
        <FontAwesomeIcon 
          icon={faTimes} 
          size={14} 
          color="rgba(44, 44, 44, 0.4)" 
        />
      </TouchableOpacity>
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    alignItems: 'center',
    padding: 0, // Padding géré par content
    marginBottom: 12,
    borderWidth: 1.5,
    width: '100%',
    maxWidth: 600, // Limiter la largeur pour meilleure lisibilité
    ...Platform.select({
      web: {
        borderTopLeftRadius: 10,
        borderTopRightRadius: 6,
        borderBottomRightRadius: 10,
        borderBottomLeftRadius: 6,
        boxShadow: '0 2px 6px rgba(0, 0, 0, 0.08)',
      },
      default: {
        borderRadius: 10,
        shadowColor: '#000',
        shadowOffset: {width: 0, height: 1},
        shadowOpacity: 0.08,
        shadowRadius: 3,
        elevation: 2,
      },
    }),
    overflow: 'hidden',
  },
  indicator: {
    width: 4,
    height: '100%',
    flexShrink: 0,
  },
  content: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'flex-start',
    padding: 14,
    paddingLeft: 12,
    gap: 10,
  },
  icon: {
    marginTop: 2,
    flexShrink: 0,
    opacity: 0.7,
  },
  message: {
    flex: 1,
    fontSize: 14,
    lineHeight: 20,
    fontFamily: Fonts.reading, // Police narrative
    letterSpacing: 0.1,
  },
  dismissButton: {
    padding: 12,
    paddingRight: 14,
    alignSelf: 'flex-start',
    opacity: 0.5,
    ...Platform.select({
      web: {
        cursor: 'pointer',
        transition: 'opacity 0.2s ease',
      },
    }),
  },
});

export default GameNotification;
